import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { defaultStyle } from "components/Modals";
import { getBatteryForecastHistory } from "api/resources/batteries";
import { BatteryForecastHistory } from "api/models/Battery";

interface BatteryForecastHistoryModalProps {
  forecastId: number;
  timeLabel: string;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const BatteryForecastHistoryModal = ({
  forecastId,
  timeLabel,
}: BatteryForecastHistoryModalProps) => {
  const [history, setHistory] = useState<BatteryForecastHistory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getBatteryForecastHistory(forecastId)
      .then((response) => {
        setHistory(response.data || []);
      })
      .catch((error) => {
        console.error("Error fetching battery forecast history:", error);
        setHistory([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [forecastId]);

  return (
    <Box sx={{ ...defaultStyle, width: 900 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Forecast history for {timeLabel}
      </Typography>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : history.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
            fontSize: "0.9rem",
            color: grey[600],
          }}
        >
          No history available
        </Box>
      ) : (
        <Box sx={{ maxHeight: 400, overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: grey[200],
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: grey[200],
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  Planned transaction (kW)
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: grey[200],
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  Planned price
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: grey[200],
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  Executed transaction (kW)
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: grey[200],
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  Executed price
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: grey[200],
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  Changed by
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((entry) => (
                <TableRow
                  key={entry.id}
                  sx={{
                    "&:nth-of-type(odd)": { backgroundColor: grey[50] },
                    "&:hover": { backgroundColor: grey[100] },
                  }}
                >
                  <TableCell>
                    {dayjs(entry.createdAt).format("DD.MM.YYYY HH:mm:ss")}
                  </TableCell>
                  <TableCell align="right">
                    {entry.plannedTransaction ?? "-"}
                  </TableCell>
                  <TableCell align="right">
                    {entry.plannedTransactionPrice != null
                      ? entry.plannedTransactionPrice.toFixed(2)
                      : "-"}
                  </TableCell>
                  <TableCell align="right">
                    {entry.executedTransaction ?? "-"}
                  </TableCell>
                  <TableCell align="right">
                    {entry.executedTransactionPrice != null
                      ? entry.executedTransactionPrice.toFixed(2)
                      : "-"}
                  </TableCell>
                  <TableCell>{entry.createdBy}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default BatteryForecastHistoryModal;
